import { Link } from "react-router-dom";
import { Smartphone, Wrench, Compass, ArrowUpRight } from "lucide-react";
import { KineticText } from "@/components/KineticText";
import { Reveal } from "@/components/Reveal";
import { MagneticButton } from "@/components/MagneticButton";

const services = [
  {
    n: "01",
    icon: Smartphone,
    title: "Création d'applications",
    text: "Développement web et mobile (iOS/Android) pour tous domaines (e-commerce, SaaS, B2B/B2C, santé, éducation).",
    tags: ["Web", "iOS / Android", "SaaS", "E-commerce"],
  },
  {
    n: "02",
    icon: Wrench,
    title: "Suivi et Maintenance",
    text: "Accompagnement post-lancement, suivi technique, correctifs et évolutions.",
    tags: ["Suivi technique", "Correctifs", "Évolutions"],
  },
  {
    n: "03",
    icon: Compass,
    title: "Consulting & Freelance",
    text: "Consultant technique pour projets en cours ou en freelance. Conseil sur l'architecture, stack technique et optimisation.",
    tags: ["Architecture", "Stack technique", "Optimisation"],
  },
];

export default function Services() {
  return (
    <main className="pt-32 pb-28" data-testid="services-page">
      <section className="px-6 lg:px-10 max-w-7xl mx-auto">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-blue-500 mb-6">Services</p>
        <h1 className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-[5rem] tracking-tighter leading-[0.95]" data-testid="services-title">
          <KineticText delay={0.15} lines={[<>Trois expertises,</>, <><span className="text-blue-500">un seul interlocuteur.</span></>]} />
        </h1>
        <p className="mt-8 max-w-xl text-lg text-slate-300 leading-relaxed">
          Du premier prototype à la maintenance long terme, un accompagnement
          technique complet pour votre produit.
        </p>
      </section>

      <section className="mt-24 px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 border border-slate-800/80 divide-y md:divide-y-0 md:divide-x divide-slate-800/80">
          {services.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1} className="h-full">
              <article
                data-testid={`service-card-${i}`}
                className="group relative h-full p-10 lg:p-12 hover:bg-blue-600/5 transition-colors overflow-hidden"
              >
                <span className="absolute top-6 right-8 font-heading font-extrabold text-[5rem] leading-none text-slate-900 select-none group-hover:text-blue-950 transition-colors" aria-hidden="true">
                  {s.n}
                </span>
                <s.icon size={30} className="text-blue-500 relative" />
                <h2 className="relative font-heading font-bold text-2xl mt-8 mb-4 group-hover:text-blue-400 transition-colors">
                  {s.title}
                </h2>
                <p className="relative text-slate-400 leading-relaxed">{s.text}</p>
                <div className="relative mt-8 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span key={t} className="font-mono text-[11px] tracking-wide text-slate-500 border border-slate-800 rounded-full px-3 py-1">
                      {t}
                    </span>
                  ))}
                </div>
                <span aria-hidden="true" className="absolute bottom-0 left-0 h-[2px] w-0 bg-blue-600 group-hover:w-full transition-[width] duration-500" />
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28 px-6 lg:px-10 max-w-7xl mx-auto">
        <Reveal>
          <div className="border border-slate-800/80 p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 relative overflow-hidden">
            <div aria-hidden="true" className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-blue-600/10 blur-[100px]" />
            <div className="relative">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Besoin d'un devis ?</h2>
              <p className="mt-3 text-slate-400 max-w-md">
                Décrivez votre projet, je vous réponds rapidement avec une estimation claire.
              </p>
            </div>
            <MagneticButton className="relative">
              <Link
                to="/contact"
                data-testid="services-cta-contact"
                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 transition-colors"
              >
                Demander un devis
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </MagneticButton>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
